package com.patronat.top.features.venues.presentation

import com.patronat.top.core.base.ViewEvent
import com.patronat.top.core.base.ViewSideEffect
import com.patronat.top.core.base.ViewState
import com.patronat.top.dto.CreateVenueRequest
import com.patronat.top.dto.VenueDraftState
import com.patronat.top.dto.VenueAssigneeResponse
import com.patronat.top.dto.VenueAiFinding
import com.patronat.top.dto.UserRole
import com.patronat.top.dto.venues.VenueStatus
import com.patronat.top.library.media.data.FileSource
import com.patronat.top.ui.models.OwnerInfoData
import com.patronat.top.ui.models.VenueGalleryPhoto
import com.patronat.top.ui.models.VenueLocationData
import com.patronat.top.ui.models.VenueMainInfoData
import com.patronat.top.ui.models.VenueServiceItemData
import com.patronat.top.ui.models.WorkScheduleDay

sealed interface VenueFormMode {
    data object Create : VenueFormMode
    data class CreateFromAi(val draft: CreateVenueRequest) : VenueFormMode
    data class Edit(val venueId: String) : VenueFormMode
}

data class VenueScheduleDayLabels(
    val monday: String,
    val tuesday: String,
    val wednesday: String,
    val thursday: String,
    val friday: String,
    val saturday: String,
    val sunday: String
)

data class VenuePickedFile(
    val fileSource: FileSource,
    val mimeType: String,
    val fileName: String
)

data class VenueFormState(
    val isInitialized: Boolean = false,
    val mode: VenueFormMode = VenueFormMode.Create,
    val currentUserRole: UserRole? = null,
    val isLoading: Boolean = false,
    val isModeratorAssigneesLoading: Boolean = false,
    val isModeratorReassigning: Boolean = false,
    val isSubmitting: Boolean = false,
    val isGalleryUploading: Boolean = false,
    val isServicesFileUploading: Boolean = false,
    val isModeratorMenuExpanded: Boolean = false,
    val selectedTab: VenueFormTab = VenueFormTab.Venue,
    val venueStatus: VenueStatus? = null,
    val draftState: VenueDraftState? = null,
    val ownerId: String? = null,
    val venueModeratorId: String? = null,
    val moderatorAssignees: List<VenueAssigneeResponse> = emptyList(),
    val ownerInfo: OwnerInfoData = OwnerInfoData(),
    val ownerReadOnly: Boolean = true,
    val mainInfo: VenueMainInfoData = VenueMainInfoData(),
    val scheduleDays: List<WorkScheduleDay> = emptyList(),
    val locationData: VenueLocationData = VenueLocationData(),
    val venueMarkers: List<VenueMapMarker> = emptyList(),
    val galleryPhotos: List<VenueGalleryPhoto> = emptyList(),
    val services: List<VenueServiceItemData> = emptyList(),
    val aiFindings: List<VenueAiFinding> = emptyList(),
    val aiFlaggedFieldPaths: Set<String> = emptySet(),
    val aiServiceErrorIds: Set<String> = emptySet(),
    val aiGalleryErrorIds: Set<String> = emptySet(),
    val attachedServicesFileName: String? = null,
    val servicesPriceListMediaId: String? = null,
    val servicesPriceListUrl: String? = null,
    val nextServiceIndex: Int = 1
) : ViewState

sealed interface VenueFormEvent : ViewEvent {
    data class Initialize(
        val mode: VenueFormMode,
        val scheduleDayLabels: VenueScheduleDayLabels,
        val currentUserRole: UserRole? = null
    ) : VenueFormEvent

    data class OnSelectedTabChanged(val tab: VenueFormTab) : VenueFormEvent
    data class OnOwnerInfoChanged(val data: OwnerInfoData) : VenueFormEvent
    data class OnMainInfoChanged(val data: VenueMainInfoData) : VenueFormEvent
    data class OnScheduleChanged(val days: List<WorkScheduleDay>) : VenueFormEvent
    data class OnLocationDataChanged(val data: VenueLocationData) : VenueFormEvent
    data class OnGalleryPhotosChanged(val photos: List<VenueGalleryPhoto>) : VenueFormEvent
    data class OnServicesChanged(val services: List<VenueServiceItemData>) : VenueFormEvent
    data class OnServicesFileSelected(val fileName: String) : VenueFormEvent

    data object OnGalleryAddClicked : VenueFormEvent
    data class OnGalleryPhotoPicked(val photo: VenueGalleryPhoto) : VenueFormEvent
    data class OnGalleryFilePicked(val file: VenuePickedFile) : VenueFormEvent
    data object OnUploadServicesFileClicked : VenueFormEvent
    data class OnServicesFilePicked(val file: VenuePickedFile) : VenueFormEvent
    data object OnServicesFileRemoveClicked : VenueFormEvent
    data object OnAddServiceClicked : VenueFormEvent

    data object OnSaveServicesDraftClicked : VenueFormEvent
    data object OnSubmitServicesClicked : VenueFormEvent
    data object OnVenueCancelClicked : VenueFormEvent
    data object OnVenueSubmitClicked : VenueFormEvent
    data object OnVenueRejectClicked : VenueFormEvent
    data object OnModeratorSelectorClicked : VenueFormEvent
    data object OnDismissModeratorMenu : VenueFormEvent
    data class OnModeratorSelected(val moderatorId: String) : VenueFormEvent
}

sealed interface VenueFormEffect : ViewSideEffect {
    data object OpenGalleryImagePicker : VenueFormEffect
    data object OpenServicesPdfPicker : VenueFormEffect
    data object NavigateBack : VenueFormEffect
}
